      
<template>
  <div>
    <el-card class="chart-card">
      <h1>Pignoo AutoDatabase - 自动构建数据库（MySQL举例）</h1>
      <p>
        <el-text>Pignoo AutoDatabase是一个Pignoo根据实体自动构建、修改数据库的工具。需要单独引入：</el-text>
      </p>
      <div>
        <pre><code class="language-xml">{{ code1 }}</code></pre>
      </div>
      <p>
        <el-text>Pignoo的设计理念是强化面向对象概念、弱化数据库存储概念。但Pignoo程序的运行，还是需要提前构建好配套的数据库。</el-text>
        <el-text>为了实现纯更纯粹的面向对象化编程，为了将class视为“数据承载的结构”而非“数据库表的映射”，</el-text>
        <el-text>引入Pignoo AutoDatabase后，工具可以根据class自动创建、修改表结构，以匹配Pignoo的运行。</el-text>
        <el-text>甚至，Pignoo AutoDatabase可以让编程者完全不操作数据库的情况下，搭建起一个可持久化数据的项目。</el-text>
      </p>
      <h1>对比代码生成器</h1>
      <p>
        <el-text>市面上有许多代码生成器，从比较原始的MyBatis Generator，到一些二开框架的代码生成器（设置连Java带Web页面都成套生成）。</el-text>
        <el-text>它们选择的方案均是开发者需要先构建好数据库，代码生成器会根据数据库整套生成代码。</el-text>
        <el-text>这往往会引入几个问题：</el-text>
        <br />
        <el-text>1. 代码不是自己写的，维护起来较生疏，尤其项目搁置一段时间后再去改模版代码，很困难；</el-text>
        <br />
        <el-text>2. 模板代码往往是机械的增删改查，不具有业务内容，且有很多无效代码；</el-text>
        <br />
        <el-text>3. 越大的模板，生成的代码时间相互依赖的越繁琐，改动起来越难（比如改了个API，模板页面会跟着报错）；</el-text>
        <br />
        <el-text>4. 数据库变动时，代码需要随之变动，重新生成会掩盖之前改动的逻辑，手动在之前生成的各处代码增加字段又很繁琐。</el-text>
      </p>
      <p>
        <el-text>综上，除非特定场景下，本人是不愿意使用模版生成代码的。更愿意以精炼、快捷的方式针对业务需求开发对应代码。</el-text>
      </p>
      <p>
        <el-text>Pignoo AutoDatabase反其道而行。通过Java代码自动检查、构建、修改数据库。不是让代码匹配数据库，而是让数据库匹配代码。</el-text>
        <el-text>这样做的优势在于：</el-text>
        <br />
        <el-text>1. 只开发有用的代码，配合Pignoo简练的操作数据库方式，代码里全是干货；</el-text>
        <br />
        <el-text>2. 增加功能时，编程者只需要在Java对象关系之间思考数据存储方式，纯抽象，无需思考“表”的概念；</el-text>
        <br />
        <el-text>3. 数据库是跟随实体的，程序在迁移到新环境时，Pignoo AutoDatabase能承担类似FlyWay的数据库版本管理的作用。</el-text>
      </p>
      <p>
        <el-text>Pignoo AutoDatabase也有显著的缺点，是否使用Pignoo AutoDatabase需要认真考量：</el-text>
        <br />
        <el-text>1. 一般程序更倾向数据至上，数据库、数据结构是一切的基石，但Pignoo AutoDatabase在试图轻视数据库的地位；</el-text>
        <br />
        <el-text>2. Pignoo AutoDatabase很难做到精准控制数据结构，例如：String对应数据类型varchar还是text、字段长度是多少，</el-text>
        <el-text>这些有关运行效率的精细化配置要想面面俱到，需要配置复杂的Pignoo AutoDatabase规则。</el-text>
      </p>
      <p>
        <el-text tag="b">总结：Pignoo AutoDatabase用于简化数据库构建，在单数据库、无需太在意数据库字段细节优化的项目中尤为适用。</el-text>
      </p>
      <h1>配置</h1>
      <div style="width: 700px;margin: auto;">
        <el-table :data="configTable" style="width: 100%;" size="small" border="true">
          <el-table-column prop="param" label="配置项" />
          <el-table-column prop="note" label="可选值" />
          <el-table-column prop="default" label="默认值" />
        </el-table>
      </div>
      <h1>使用</h1>
      <p>
        <el-text>Pignoo AutoDatabase本质是一个扫描器：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code2 }}</code></pre>
      </div>
      <p>
        <el-text>scaner.scan()时，会获取实体并匹配数据库、构建数据库。通常，是在项目启动时，扫描一遍全部实体。</el-text>
        <el-text>但是scaner可以是多例的，且可以缓存下来的，这就让scaner有了更多玩法。</el-text>
        <el-text>例如：定期扫描数据库，防止代码运行期间数据库结构被外部修改；不同包下的实体配置不同的扫描规则。</el-text>
      </p>
      <p>
        <el-text tag="b">扫描规则与注意事项</el-text>
      </p>
      <p>
        <el-text>1. 只识别一般public类和public静态内部类</el-text>
        <br />
        <el-text>2. 如果配置annotationClassOnly=false，会扫描到包下全部的类，扫到非JavaBean可能会出错</el-text>
        <br />
        <el-text style="margin-left: 10px;">2.1. annotationClassOnly=false时，要精确的配置到实体所在包</el-text>
        <br />
        <el-text style="margin-left: 10px;">2.2. annotationClassOnly=true时，可以配置扫全局，例如：扫描Application.class且递归扫描</el-text>
        <br />
        <el-text>3. classesForScanPackage是扫描类所在的包，不是只扫描这一个类</el-text>
      </p>
      <h1>自动执行的SQL举例</h1>
      <div>
        <pre><code class="language-java">{{ code3 }}</code></pre>
      </div>
      <p>
        <el-text>随着上面Class文件的修改，Pignoo AutoDatabase会逐步做出如下建议并执行SQL：</el-text>
      </p>
      <div>
        <pre><code class="language-sql">{{ code4 }}</code></pre>
      </div>
      <h1>buildMode配置项</h1>
      <p>
        <el-text>【CAREFULLY】谨慎模式：不做任何操作，只给出提示。</el-text>
        <br />
        <el-text>【SAFELY】安全模式：自动执行新增操作，即使自动操作与预期有偏差，也不会影响原有数据。</el-text>
        <br />
        <el-text>【USABILITY】易用模式：自动执行新增和修改操作。</el-text>
        <br />
        <el-text>【RADICALLY】激进模式：尽可能执行任何操作，包括删除。</el-text>
      </p>
      <p>
        <el-text>请关注上面例子中的code字段，4次操作分别对应【建表】、【增加列】、【修改列】、【删除列】。</el-text>
      </p>
      <div style="width: 700px;margin: auto;">
        <el-table :data="buildModeTable" style="width: 100%;" size="small" border="true">
          <el-table-column prop="buildMode" label="buildMode" />
          <el-table-column prop="addTable" label="自动执行建表" align="center" />
          <el-table-column prop="addColumn" label="自动执行增加列" align="center" />
          <el-table-column prop="setColumn" label="自动执行修改列" align="center" />
          <el-table-column prop="delColumn" label="自动执行删除列" align="center" />
        </el-table>
      </div>
      <p>
        <el-text>Warning：给出SQL提示但不执行。</el-text>
        <br />
        <el-text>Advise：给出SQL提示且执行。</el-text>
      </p>
      <p>
        <el-text tag="b">配合breakRunning配置项</el-text>
        <br />
        <el-text>1. breakRunning=true时遇到Warning：不执行Advise SQL，不执行Warning SQL，以log.error()日志输出SQL，抛出异常。</el-text>
        <br />
        <el-text>2. breakRunning=true时只遇到Advise：执行Advise SQL，无Warning SQL，以log.warn()日志输出SQL，不抛出异常。</el-text>
        <br />
        <el-text>3. breakRunning=false时遇到Warning：执行Advise SQL，不执行Warning SQL，以log.warn()日志输出SQL，不抛出异常。</el-text>
        <br />
        <el-text>4. breakRunning=false时只遇到Advise：执行Advise SQL，无Warning SQL，以log.warn()日志输出SQL，不抛出异常。（同2）</el-text>
        <br />
        <el-text>5. SQL执行报错时，无论breakRunning配置如何，都会抛出异常。</el-text>
      </p>
      <h1>数据规模与typeMapper配置项</h1>
      <p>
        <el-text>请关注上面例子中的key、value字段，4次操作数据规模分别是【小型】、【中型】、【大型】，code字段一直采用的是【默认】。</el-text>
      </p>
      <p>
        <el-text>这是默认的数据规模配置：</el-text>
      </p>
      <div style="width: 500px;margin: auto;">
        <el-table :data="presetScaleTable" style="width: 100%;" size="small" border="true">
          <el-table-column prop="presetScale" label="presetScale" />
          <el-table-column prop="value" label="int值" align="center" width="70" />
          <el-table-column prop="string" label="String" align="center" />
          <el-table-column prop="bigDecimal" label="BigDecimal" align="center" />
        </el-table>
      </div>
      <p>
        <el-text tag="b">配合strictColumnType配置项</el-text>
      </p>
      <p>
        <el-text>strictColumnType=false，数据库中已存在的列的类型能够跟Java字段类型正确映射即可。例如：Java的Integer可以映射数据库的bigint。</el-text>
        <br />
        <el-text>strictColumnType=true，数据类型、数据规模需对应，不对应会提示修改。</el-text>
      </p>
      <p>
        <el-text tag="b">typeMapper配置项</el-text>
      </p>
      <p>
        <el-text>typeMapper用于自定义字段类型映射规则。typeMapper是一个FunctionalInterface。例如：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code5 }}</code></pre>
      </div>
      <p>
        <el-text tag="b">什么是数据规模（presetScale）？</el-text>
      </p>
      <p>
        <el-text>数据规模，是Pignoo中的一个用于描述数据存储量、精度的一个字段。数据规模是int类型的。</el-text>
      </p>
      <p>
        <el-text>数据规模是用于描述字段使用场景、精确控制映射的数据库列数据类型、大小的。在没有苛刻要求的项目中，默认的3种数据规模足以覆盖绝大多数使用场景。</el-text>
      </p>
      <p>
        <el-text>数据规模不是varchar(255)中的“255”！数据规模是用int表示的“枚举”，因为希望用户可以自由扩展数据规模，所以设定为int类型。</el-text>
        <el-text>使用数据规模时，建议定义一个或多个常量Class，存放数据规模。例如：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code6 }}</code></pre>
      </div>
      <div>
        <pre><code class="language-java">{{ code7 }}</code></pre>
      </div>
      <div>
        <pre><code class="language-java">{{ code8 }}</code></pre>
      </div>
      <div>
        <pre><code class="language-java">{{ code9 }}</code></pre>
      </div>
      <h1>由JavaBean映射到数据库</h1>
      <p>
        <el-text>由JavaBean映射到数据库，是按照typeMapper规则映射的，这是默认的typeMapper：</el-text>
      </p>
      <div>
        <pre><code class="language-text">{{ code10 }}</code></pre>
      </div>
      <h1>由数据库映射到JavaBean</h1>
      <p>
        <el-text>由数据库映射到JavaBean，目的是在strictColumnType=false时，验证数据库列类型是否满足与JavaBean字段类型的条件：</el-text>
      </p>
      <div>
        <pre><code class="language-text">{{ code11 }}</code></pre>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'

onMounted(() => {
  if (window.hljs) {
    window.hljs.highlightAll()
  }
})

const code1 = `<dependency>
  <groupId>com.xuesinuo</groupId>
  <artifactId>pignoo-autodatabase</artifactId>
  <version>1.1.6</version>
</dependency>`

const code2 = `PignooConfig pignooConfig = new PignooConfig(); // Pignoo配置
EntityScanConfig entityScanConfig = new EntityScanConfig(); // Pignoo AutoDatabase配置
entityScanConfig.setPackages(new String[] { "com.demo.entity" }); // 配置存放实体的包
entityScanConfig.setStrictColumnType(true); // 此配置项后续会做出解释

EntityScaner scaner = new EntityScaner(dataSource, pignooConfig, entityScanConfig);
scaner.scan(); // 扫描并构建数据库`

const configTable = [
  { param: 'scanChildPackages', note: '是否递归扫描子包', default: 'true' },
  { param: 'annotationClassOnly', note: '只扫描带有@Table的类', default: 'true' },
  { param: 'packages', note: 'String[]，要扫描的包路径', default: '[]' },
  { param: 'classesForScanPackage', note: '自定义Class[]，扫描这些类所在包。和packages同时生效，取并集', default: '[]' },
  { param: 'buildMode', note: 'CAREFULLY | SAFELY | USABILITY | RADICALLY', default: 'CAREFULLY' },
  { param: 'breakRunning', note: '是否打断运行：再遇到无法容忍的问题时，抛出异常', default: 'true' },
  { param: 'typeMapper', note: '自定义类型映射器，将Java类型转为SQL类型', default: 'null' },
  { param: 'strictColumnType', note: '严格限制数据库字段类型与Pignoo配置一致', default: 'false' },
]

const code3 = `// 第1步：新增Class
@Table("autodatabase_test01")
@Data
public class AutodatabaseTest01 {
  private Long id;
  private String name;
}}

// 第2步：增加字段
@Table("autodatabase_test01")
@Data
public class AutodatabaseTest01 {
  private Long id;
  private String name;
  private String code; // 新增code字段
  @Column(scale = Column.PresetScale.SMALL)
  private String key;
  @Column(scale = Column.PresetScale.SMALL)
  private BigDecimal value;
}

// 第3步：修改字段数据类型
@Table("autodatabase_test01")
@Data
public class AutodatabaseTest01 {
  private Long id;
  private String name;
  private Long code; // 修改数据类型
  @Column(scale = Column.PresetScale.MEDIUM)
  private String key;
  @Column(scale = Column.PresetScale.MEDIUM)
  private BigDecimal value;
}

// 第4步：删除字段
@Table("autodatabase_test01")
@Data
public class AutodatabaseTest01 {
  private Long id;
  private String name;
  // 去除code字段
  @Column(scale = Column.PresetScale.LARGE)
  private String key;
  @Column(scale = Column.PresetScale.LARGE)
  private BigDecimal value;
}`

const code4 = `-- 第1步：创建表
CREATE TABLE \`autodatabase_test01\` ( \`id\` bigint NOT NULL AUTO_INCREMENT, \`name\` varchar(255) DEFAULT NULL, PRIMARY KEY (\`id\`) ) ;

-- 第2步：增加列
ALTER TABLE \`autodatabase_test01\` ADD COLUMN \`code\` varchar(255) NULL ;
ALTER TABLE \`autodatabase_test01\` ADD COLUMN \`key\` varchar(255) NULL ;
ALTER TABLE \`autodatabase_test01\` ADD COLUMN \`value\` decimal(16,4) NULL ;

-- 第3步：修改列数据类型
ALTER TABLE \`autodatabase_test01\` MODIFY COLUMN \`code\` bigint NULL ;
ALTER TABLE \`autodatabase_test01\` MODIFY COLUMN \`key\` text NULL ;
ALTER TABLE \`autodatabase_test01\` MODIFY COLUMN \`value\` decimal(32,8) NULL ;

-- 第4步：删除列
ALTER TABLE \`autodatabase_test01\` DROP COLUMN \`code\` ;
ALTER TABLE \`autodatabase_test01\` MODIFY COLUMN \`key\` longtext NULL ;
ALTER TABLE \`autodatabase_test01\` MODIFY COLUMN \`value\` decimal(64,16) NULL ;`

const buildModeTable = [
  { buildMode: 'CAREFULLY（谨慎）', addTable: 'Warning', addColumn: 'Warning', setColumn: 'Warning', delColumn: 'Warning' },
  { buildMode: 'SAFELY（安全）', addTable: 'Advise', addColumn: 'Advise', setColumn: 'Warning', delColumn: 'Warning' },
  { buildMode: 'USABILITY（易用）', addTable: 'Advise', addColumn: 'Advise', setColumn: 'Advise', delColumn: 'Warning' },
  { buildMode: 'RADICALLY（激进）', addTable: 'Advise', addColumn: 'Advise', setColumn: 'Advise', delColumn: 'Advise' },
]

const presetScaleTable = [
  { presetScale: '默认', value: "0", string: 'varchar(255)', bigDecimal: 'decimal(32,8)' },
  { presetScale: 'SMALL（小型）', value: "1", string: 'varchar(255)', bigDecimal: 'decimal(16,4)' },
  { presetScale: 'MEDIUM（中型）', value: "2", string: 'text', bigDecimal: 'decimal(32,8)' },
  { presetScale: 'LARGE（大型）', value: "3", string: 'longtext', bigDecimal: 'decimal(64,16)' },
]

const code5 = `// 入参javaType为字段类型；scale为标注的数据规模；field为反射来的整个字段对象
// field中其实是包含javaType,、scale的。javaType, scale是Pignoo更推荐使用的规则参数；field参数则是为了更好的扩展性
entityScanConfig.setTypeMapper((javaType, scale, field) -> {
  
  // 仅对String类型进行设置
  if (String.class.isAssignableFrom(javaType)) {
    // 字段以number结尾，且数据规模为默认（未设置数据规模），则使用char(20)
    if (field.getName().toLowerCase().endsWith("number") && scale == 0) {
      return "char(20)";
    }
    
    // 数据规模在0～255之间，使用varchar并将数据规模作为长度
    if (scale > 0 && scale <= 255) {
      return "varchar(" + scale + ")";
    } 
        
    // 数据规模为默认，或设置了错误的数据规模，使用varchar(255)
    else if (scale <= 0) {
      return "varchar(255)";
    } 
    
    // 数据规模大于255，使用longtext
    else {
      return "longtext";
    }
  }

  // 返回null，未匹配到的类型将运行Pignoo的默认匹配规则
  return null;
});`

const code6 = `public class StringPresetScale {
  /** 用户名 */
  public static final int USERNAME = 1;
  /** 产品名 */
  public static final int PRODUCT_NAME = 2;
  /** 文章 */
  public static final int ARTICLE = 3;
}`

const code7 = `public class BigDecimalPresetScale {
  /** 钱 */
  public static final int MONEY = 1;
  /** 账 */
  public static final int ACCOUNT = 2;
  /** 产品数量（例如记录单位是吨） */
  public static final int QUANTITY = 3;
}`

const code8 = `entityScanConfig.setTypeMapper((javaType, scale, field) -> {
  if (String.class.isAssignableFrom(javaType)) {
    if (scale == StringPresetScale.USERNAME) {
      return "varchar(20)"; // 用户名
    } else if (scale == StringPresetScale.PRODUCT_NAME) {
      return "varchar(50)"; // 产品名
    } else if (scale == StringPresetScale.ARTICLE) {
      return "longtext"; // 文章
    }
  }
  if (BigDecimal.class.isAssignableFrom(javaType)) {
    if (scale == StringPresetScale.MONEY) {
      return "decimal(12,2)"; // 钱，精确到分
    } else if (scale == StringPresetScale.ACCOUNT) {
      return "decimal(14,4)"; // 账，精确到豪
    } else if (scale == StringPresetScale.QUANTITY) {
      return "decimal(20,6)"; // 数量，精确到克
    }
  }
  return null;
});`

const code9 = `/** 销售员 */
@Data
public class Salesperson {
  private Long id;
  /** 销售员用户名 */
  @Column(scale = StringPresetScale.USERNAME)
  private String username;
  /** 销售员负责产品 */
  @Column(scale = BigDecimalPresetScale.PRODUCT_NAME)
  private String productName;
  /** 销售员个人简介 */
  @Column(scale = BigDecimalPresetScale.ARTICLE)
  private String personalProfile;

  /** 累计销售额 */
  @Column(scale = BigDecimalPresetScale.ACCOUNT)
  private BigDecimal accumulativeSales;
  /** 累计销售数量 */
  @Column(scale = BigDecimalPresetScale.QUANTITY)
  private BigDecimal accumulativeQuantity;
  /** 实际到账总金额 */
  @Column(scale = BigDecimalPresetScale.MONEY)
  private BigDecimal actualCreditedAmount;
}`

const code10 = `Long           -> bigint
Integer        -> int
Short          -> smallint
Byte           -> tinyint
Double         -> double
Float          -> float
Boolean        -> tinyint(1)
Character      -> char(1)
String         -> [Default]varchar(255) | [SMALL]varchar(255) | [MEDIUM]text | [LARGE]longtext
java.util.Date -> datetime
Instant        -> timestamp
LocalDate      -> date
LocalTime      -> time
LocalDateTime  -> datetime
ZonedDateTime  -> timestamp
OffsetDateTime -> timestamp
OffsetTime     -> time
BigDecimal     -> [Default]decimal(32,8) | [SMALL]decimal(16,4) | [MEDIUM]decimal(32,8) | [LARGE]decimal(64,16)
BigInteger     -> decimal(64,0)
byte[]         -> blob
Enum           -> varchar(255)`

const code11 = `Long           <- bigint
Integer        <- int | integer | mediumint
Short          <- smallint
Byte           <- tinyint
Double         <- double | float | real
Float          <- float | real
Boolean        <- tinyint | bit | boolean
Character      <- char | varchar
String         <- char | varchar | text | tinytext | mediumtext | longtext | json
BigInteger     <- bigint | decimal | numeric
BigDecimal     <- decimal | numeric
java.util.Date <- date | datetime | timestamp | time
LocalDate      <- date
LocalTime      <- time
LocalDateTime  <- datetime | timestamp
Instant        <- datetime | timestamp
ZonedDateTime  <- datetime | timestamp
OffsetDateTime <- datetime | timestamp
OffsetTime     <- time
byte[]         <- blob | tinyblob | mediumblob | longblob | binary | varbinary
Enum           <- enum | char | varchar`

</script>

<style lang="less"></style>